import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { makeStyles } from '@material-ui/core/styles'
import { Button, CircularProgress, TextField, Typography } from '@material-ui/core'

import { settings } from '../../store/constants'

const GOOGLE_PLACE_ID = 'ChIJx-etkF4FdkgRL3u0Hrbp8jQ'

// Google's own write-a-review form, nothing in front of it. Keep this equal
// to AppSecrets#google_review_url.
const GOOGLE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '60vh',
    padding: theme.spacing(4, 2),
    maxWidth: 480,
    margin: '0 auto'
  },
  starRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(3, 0)
  },
  star: {
    fontSize: 44,
    cursor: 'pointer',
    transition: 'color 0.15s, transform 0.15s',
    background: 'none',
    border: 'none',
    padding: 0,
    lineHeight: 1,
    '&:hover': {
      transform: 'scale(1.2)'
    }
  },
  starActive: {
    color: '#f59e0b'
  },
  starInactive: {
    color: '#d4d4d8'
  },
  noteField: {
    width: '100%',
    marginTop: theme.spacing(2)
  },
  submitBtn: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 5),
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none'
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    padding: theme.spacing(1.5, 2),
    color: '#991b1b',
    marginTop: theme.spacing(2),
    width: '100%',
    textAlign: 'center'
  },
  // Thank-you styles
  thankYouStars: {
    fontSize: 36,
    margin: theme.spacing(2, 0),
    textAlign: 'center'
  },
  googleCard: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: 'center',
    marginTop: theme.spacing(3),
    width: '100%'
  },
  googleBtn: {
    marginTop: theme.spacing(1.5),
    background: '#22c55e',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1.2, 3),
    '&:hover': {
      background: '#16a34a'
    }
  },
  backLink: {
    marginTop: theme.spacing(3),
    color: '#6b7280',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 14
  }
}))

export const FeedbackPage = React.memo(() => {
  const classes = useStyles()
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])

  const token = params.get('token')
  const preselectedRating = parseInt(params.get('rating'), 10) || 0

  const [rating, setRating] = useState(preselectedRating > 0 && preselectedRating <= 5 ? preselectedRating : 0)
  const [hoverRating, setHoverRating] = useState(0)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [finalRating, setFinalRating] = useState(0)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!rating) {
      setError('Please select a rating.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const res = await fetch(`${settings.host}/api/v2/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, note: note.trim() })
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.')
        setSubmitting(false)
        return
      }

      setFinalRating(data.rating || rating)
      setSubmitted(true)
    } catch {
      setError('Network error. Please try again.')
      setSubmitting(false)
    }
  }, [token, rating, note])

  if (!token) {
    return (
      <div className={classes.root}>
        <Helmet title="Feedback" />
        {/* Feedback tokens have no expiry (Order#generate_feedback_token!), so
            this no longer tells the customer the link "has expired", and it
            ends on the way to get a working one rather than on the fault. */}
        <Typography variant="h5" gutterBottom>That link is not valid</Typography>
        <Typography color="textSecondary">
          Open the rating link in your most recent message from us, or email support@gowashmycar.com and we
          will send a fresh one.
        </Typography>
        <a className={classes.backLink} href="/">Back to GoWashMyCar</a>
      </div>
    )
  }

  if (submitted) {
    // One ask, the same ask, for everybody.
    //
    // Until 2026-10-04 this card read differently depending on the rating,
    // and before that the Google link was shown only to 4-5 star raters.
    // Picking who may leave a review by how they privately rated us is
    // selective solicitation: prohibited by Google's fake-engagement policy
    // and treated the same way by the FTC's 2024 rule and the UK DMCC Act.
    // Nothing here may read finalRating again.
    //
    // No suggested review text either. Handing the customer words to paste
    // is what Google's "templated review" filters look for, and it is their
    // review, not ours.
    return (
      <div className={classes.root}>
        <Helmet title="Thank You" />
        <Typography variant="h5" gutterBottom>Thanks for the feedback 🙏</Typography>

        <div className={classes.thankYouStars}>
          {[1, 2, 3, 4, 5].map(s => (
            <span key={s} style={{ color: s <= finalRating ? '#f59e0b' : '#d4d4d8' }}>★</span>
          ))}
        </div>

        <div className={classes.googleCard}>
          <Typography variant="body2">
            If you have a minute, a Google review really helps us. Thank you.
          </Typography>
          <Button
            className={classes.googleBtn}
            variant="contained"
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener"
          >
            Leave a Google review
          </Button>
        </div>

        <a className={classes.backLink} href="/">Back to GoWashMyCar</a>
      </div>
    )
  }

  const displayRating = hoverRating || rating

  return (
    <div className={classes.root}>
      <Helmet title="Feedback" />
      <Typography variant="h5" gutterBottom>How was your wash?</Typography>

      <div className={classes.starRow}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className={`${classes.star} ${star <= displayRating ? classes.starActive : classes.starInactive}`}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>

      <TextField
        className={classes.noteField}
        label="Tell us more (optional)"
        multiline
        minRows={3}
        maxRows={6}
        variant="outlined"
        value={note}
        onChange={e => setNote(e.target.value)}
        inputProps={{ maxLength: 1000 }}
      />

      {error && <div className={classes.error}>{error}</div>}

      <Button
        className={classes.submitBtn}
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={submitting || !rating}
      >
        {submitting ? <CircularProgress size={22} color="inherit" /> : 'Send feedback'}
      </Button>
    </div>
  )
})
